import { api } from './api';

/**
 * Clients for the Settings → Sessions/Devices pane.
 *
 * `GET /v1/auth/sessions` lives in `services/api/internal/auth/types.go` as
 * `SessionsListResponse`. Ids come as base64url; the panel shows them as a
 * truncated hex chip for human recognition.
 *
 * `POST /v1/devices/{device_id}/revoke` is the self-service revoke landed in
 * `services/api/internal/auth/service.go:RevokeDevice`. Empty body, 204 on
 * success. The caller cannot revoke its own device — that's the auth-flow
 * sign-out path, not this one.
 */
export interface SessionDeviceWire {
  device_id: string;
  label: string;
  platform: string;
  created_at: string;
  last_seen_at: string;
  is_current: boolean;
}

export interface SessionsListResponse {
  devices: SessionDeviceWire[];
}

export async function listSessions(opts: { signal?: AbortSignal } = {}): Promise<SessionsListResponse> {
  const reqOpts = opts.signal ? { signal: opts.signal } : {};
  return api.get<SessionsListResponse>('/v1/auth/sessions', reqOpts);
}

/**
 * Revoke another device on this account. `deviceId` is the base64url string
 * exactly as `listSessions()` returns it — no re-encoding needed.
 * Resolves on 204; throws `NetworkError` for any other status (including
 * 409 if the caller tries to revoke its own device — auth-layer enforces).
 */
export async function revokeDevice(deviceId: string): Promise<void> {
  await api.post<void>(`/v1/devices/${encodeURIComponent(deviceId)}/revoke`, undefined);
}
